import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { PageHero } from "@/components/PageHero";
import { PageMeta } from "@/components/PageMeta";
import { Reveal } from "@/components/Reveal";
import { useLang } from "@/i18n";
import { ROUTES } from "@/config/links";
import { trackAssetViewed, trackCta } from "@/lib/analytics";
import { GuideRequestForm } from "./GuideRequestForm";

/**
 * Public Guide landing page (Resources → Guides → this guide), reached
 * without necessarily having taken the Presenter Archetype quiz — so
 * it offers the *generic* six-page guide (all four archetype cards on
 * page 3) via its own email gate, plus a cross-sell to the quiz for
 * anyone who wants the personalised version instead (built on the
 * quiz's own result page as `PlaybookCTA`).
 *
 * InterAct Tracking Foundation milestone (2026-10-09 pass 8) — fires
 * `asset_viewed` for the registered `next_presentation_playbook` asset
 * on mount, so a CTA that linked here (`trackCta`'s own
 * `stashCtaSource` for the "guide" destination type) is actually
 * consumed here via the shared `consumeFreshCtaSource()` — no second
 * mechanism, reuses the exact one every diagnostic already relies on.
 */
export const PresenterPlaybookGuide = () => {
  const { t, l } = useLang();
  const insideItems = ["inside1", "inside2", "inside3", "inside4"];
  // InterAct Tracking Foundation milestone (2026-10-10) — captures the
  // CTA that led here (if any), one-shot, at this SAME mount-time
  // `trackAssetViewed` call, for `GuideRequestForm`'s own shared
  // request-adapter payload below.
  const ctaNavContextRef = useRef(null);

  useEffect(() => {
    ctaNavContextRef.current = trackAssetViewed("next_presentation_playbook");
  }, []);

  return (
    <>
      <PageMeta
        titleKey="meta.presenter_playbook_guide_title"
        descriptionKey="meta.presenter_playbook_guide_description"
        path={ROUTES.presenterPlaybookGuide}
      />
      <PageHero
        testId="presenter-playbook-guide-hero"
        eyebrow={t("presenterPlaybookGuide.eyebrow")}
        headline={[t("presenterPlaybookGuide.heading")]}
        intro={t("presenterPlaybookGuide.intro")}
      />
      <section className="relative z-10 py-16 md:py-24" data-testid="presenter-playbook-guide-section">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 grid md:grid-cols-[1.1fr_0.9fr] gap-12 md:gap-16">
          <div>
            <Reveal>
              <p className="font-body text-xs uppercase tracking-[0.24em] text-terracotta mb-4">
                {t("presenterPlaybookGuide.whatsInside")}
              </p>
              <ul className="space-y-3 mb-12" data-testid="presenter-playbook-guide-inside-list">
                {insideItems.map((key) => (
                  <li key={key} className="flex items-start gap-3 font-body text-ink leading-relaxed">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-terracotta flex-shrink-0" aria-hidden="true" />
                    <span>{t(`presenterPlaybookGuide.${key}`)}</span>
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal delay={0.1}>
              <div
                className="bg-ink text-paper px-6 md:px-8 py-8 md:py-9"
                data-testid="presenter-playbook-guide-archetype-cross-sell"
              >
                <h2 className="font-serif text-xl md:text-2xl mb-2">
                  {t("presenterPlaybookGuide.archetypeCrossSellHeading")}
                </h2>
                <p className="font-body text-sm text-paper/80 leading-relaxed mb-5">
                  {t("presenterPlaybookGuide.archetypeCrossSellBody")}
                </p>
                <Link
                  to={l(ROUTES.presenterArchetype)}
                  data-testid="presenter-playbook-guide-archetype-cta"
                  className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.14em] text-mustard hover:text-paper transition-colors"
                  onClick={() => trackCta({ ctaId: "presenter-playbook-guide-archetype-cta", location: "guide_body", href: ROUTES.presenterArchetype, targetAssetId: "presenter_archetype" })}
                >
                  {t("presenterPlaybookGuide.archetypeCrossSellCta")} →
                </Link>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <GuideRequestForm ctaNavContext={ctaNavContextRef.current} />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default PresenterPlaybookGuide;
